import { FiltersConfig, SortConfig } from '@/types';
import { QueryTag } from '../ts';

export const QUERY_KEYS = {
  USER: [QueryTag.USER] as const,
  PROJECTS_DATA: [QueryTag.PROJECTS, QueryTag.SINGLE_PROJECT_DATA] as string[],
  PROJECTS: (
    workspaceId?: string,
    page?: number,
    filters?: FiltersConfig,
    sortConfig?: SortConfig,
  ) =>
    [QueryTag.PROJECTS, { workspaceId, page, ...(filters ?? {}), ...(sortConfig ?? {}) }] as const,
  WORKSPACE_MEMBERS: (workspaceId?: string) =>
    [QueryTag.WORKSPACE_MEMBERS, { workspaceId }] as const,
  WORKSPACE_GROUPS: (organizationId?: string) =>
    [QueryTag.WORKSPACE_GROUPS, { organizationId }] as const,
  ORGANIZATIONS: [QueryTag.ORGANIZATIONS] as const,
  ORGANIZATION_MEMBERS: (
    organizationId?: string,
    page?: number,
    filters?: FiltersConfig,
    sortConfig?: SortConfig,
  ) =>
    [
      QueryTag.ORGANIZATION_MEMBERS,
      { organizationId, page, ...(filters ?? {}), ...(sortConfig ?? {}) },
    ] as const,
  THEMES: (
    organizationId?: string,
    page?: number,
    filters?: FiltersConfig,
    sortConfig?: SortConfig,
  ) =>
    [QueryTag.THEMES, { organizationId, page, ...(filters ?? {}), ...(sortConfig ?? {}) }] as const,
  THEME: (organizationId?: string, themeId?: string | null) =>
    [QueryTag.THEME, { organizationId, themeId }] as const,
  CONVERSATIONS: (
    projectId?: string,
    filterId?: string | null,
    language?: string | null,
    conversationFilter?: string,
    page?: number,
    filters?: FiltersConfig,
  ) =>
    [
      QueryTag.CONVERSATIONS,
      { projectId, filterId, language, conversationFilter, page, ...(filters ?? {}) },
    ] as const,
  CONVERSATION: (projectId?: string, conversationId?: string) =>
    [QueryTag.CONVERSATION, { projectId, conversationId }] as const,
  QUESTION_TRANSCRIPTS: (
    filterId: string | null,
    categoryType: string | null,
    projectId?: string,
    questionId?: string,
    language?: string | null,
  ) =>
    [
      QueryTag.QUESTION_TRANSCRIPTS,
      { questionId, filterId, projectId, categoryType, language },
    ] as const,
  /**
   * Paginated variant — separate key so it doesn't collide with the
   * legacy (non-paginated) QUESTION_TRANSCRIPTS cache. Note they share
   * the SAME QueryTag so broad invalidations (e.g. on translation
   * delete / category override) invalidate both shapes together.
   */
  QUESTION_TRANSCRIPTS_PAGE: (params: {
    filterId: string | null;
    categoryType: string | null;
    projectId?: string;
    questionId?: string;
    language?: string | null;
    page?: number;
    limit?: number;
    search?: string;
    categoryIds?: string[];
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }) =>
    [
      QueryTag.QUESTION_TRANSCRIPTS,
      'paginated',
      {
        ...params,
        // Normalize array to sorted comma-joined string so query key
        // identity doesn't break when React re-creates the array on
        // every render.
        categoryIds:
          params.categoryIds && params.categoryIds.length
            ? [...params.categoryIds].sort().join(',')
            : undefined,
      },
    ] as const,
  PROJECT_FILTERS: (projectId?: string) => [QueryTag.PROJECT_FILTERS, { projectId }] as const,
  INTERVIEW_QUESTIONS: (projectId?: string, filterId?: string | null) =>
    [QueryTag.INTERVIEW_QUESTIONS, { projectId, filterId }] as const,
  INTERVIEW_QUESTION_INSIGHTS: (
    projectId?: string,
    questionId?: string,
    filterId?: string | null,
  ) => [QueryTag.INTERVIEW_QUESTION_INSIGHTS, { projectId, questionId, filterId }] as const,
  INTERVIEW_QUESTION_CATEGORIES: (
    projectId?: string,
    questionId?: string,
    filterId?: string | null,
  ) => [QueryTag.INTERVIEW_QUESTION_CATEGORIES, { projectId, questionId, filterId }] as const,
  INTERVIEW_OVERVIEW: (projectId?: string, filterId?: string | null) =>
    [QueryTag.INTERVIEW_OVERVIEW, { projectId, filterId }] as const,
  PROJECT_LANGUAGES: (projectId?: string) => [QueryTag.PROJECT_LANGUAGES, { projectId }] as const,
  TRANSLATION_PROGRESS: (projectId?: string, language?: string) =>
    [QueryTag.TRANSLATION_PROGRESS, { projectId, language }] as const,
  TRANSLATION_SUMMARY: (projectId?: string) =>
    [QueryTag.TRANSLATION_SUMMARY, { projectId }] as const,
  AI_SEARCH_RESULTS: (projectId?: string, filterId?: string | null) =>
    [QueryTag.AI_SEARCH_RESULTS, { projectId, filterId }] as const,
  PROJECT_METADATA: (projectId?: string) => [QueryTag.PROJECT_METADATA, { projectId }] as const,
  PROJECT_RESPONDENT_LOOKUP: (projectId?: string) =>
    [QueryTag.PROJECT_RESPONDENT_LOOKUP, { projectId }] as const,
  NEW_CONVERSATIONS_COUNT: (projectId?: string, filterId?: string | null) =>
    [QueryTag.NEW_CONVERSATIONS_COUNT, { projectId, filterId }] as const,
  SCREENING_STATS: (projectId?: string) => [QueryTag.SCREENING_STATS, { projectId }] as const,
  COMPARISONS: (projectId?: string) => [QueryTag.COMPARISONS, { projectId }] as const,
  INTEGRATIONS: (organizationId?: string) => [QueryTag.INTEGRATIONS, { organizationId }] as const,
  COLOOP_WORKSPACES: (organizationId?: string) =>
    [QueryTag.COLOOP_WORKSPACES, { organizationId }] as const,
  COLOOP_WORKSPACE_PROJECTS: (organizationId?: string, workspaceId?: string) =>
    [QueryTag.COLOOP_WORKSPACE_PROJECTS, { organizationId, workspaceId }] as const,
  PROJECT_INTEGRATION: (projectId?: string) =>
    [QueryTag.PROJECT_INTEGRATION, { projectId }] as const,
  PROJECT_EXPORTS: (projectId?: string) => [QueryTag.PROJECT_EXPORTS, { projectId }] as const,
} as const;
